import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useChangeAppointmentStatusMutation } from "../../../../api/Appointments/changeAppointmentStatus";
import { refetchAllAppointmentsApis } from "../../../../api/Appointments/refetchAllAppointmentsApis";
import clockIcon from "../../../../assets/images/icons/black-clock-01.svg";
import phoneIcon from "../../../../assets/images/icons/black-phone.svg";
import { GeneralButton, ProfileAvatar } from "../../../../components";
import useAppMediaQuery from "../../../../hooks/window/useAppMediaQuery";
import i18n from "../../../../i18n";
import { IAppointment } from "../../../../interface/appointmentInterfaces";
import { RootState } from "../../../../redux/store";
import { useToast } from "../../../../Services/ToastContext";
import {
  AppointmentActionBtn,
  DateTimeAppointmentBtn,
} from "../../../../Share";
import { truncateText } from "../../../../utils/stringsUtils";
import { formatAge } from "../../../../utils/utils";
import { useFilter } from "../../../../hooks";
import AppointmentStatusTag from "../AppointmentStatusTag/AppointmentStatusTag";
import AppointmentCancelConfirm from "../AppointmentCancelConfirm/AppointmentCancelConfirm";
import { AppointmentStatus } from "../../../../Constants";

const MyAppointmentCard: FC<{
  appointment: IAppointment;
  className?: string;
  isClickable?: boolean;
  isDisabled?: boolean;
}> = ({ appointment, className = "", isClickable = true, isDisabled }) => {
  const { t } = useTranslation();
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] =
    useState<boolean>(false);
  const [confirmCancelStatus, setConfirmCancelStatus] = useState<
    "Reject" | "Cancel"
  >();
  moment.locale(i18n.language);
  const queryClient = useQueryClient();
  const appointmentDate = moment(appointment?.Date);
  const { showToast } = useToast();

  const UserInfo = useSelector((state: RootState) => state.registration);
  const navigate = useNavigate();
  const { mutate, isPending } = useChangeAppointmentStatusMutation({
    onSuccess: () => {
      setIsCancelConfirmOpen(false);
      if (
        confirmCancelStatus === "Reject" ||
        confirmCancelStatus === "Cancel"
      ) {
        showToast(
          t(
            confirmCancelStatus === "Reject"
              ? "appointment_rejected_successfully"
              : "appointment_canceled_successfully",
          ),
          "success",
        );
        setConfirmCancelStatus(undefined);
      } else showToast(t("appointment_approved_successfully"), "success");
    },
    onError: (error) => {
      showToast(error.message, "error");
    },
  });

  const onSubmit = (values: { Note: string }) => {
    mutate(
      {
        AppointmentId: appointment?.Id,
        action: confirmCancelStatus,
        Note: values?.Note,
      } as any,
      {
        onSuccess: () => {
          setIsCancelConfirmOpen(false);
          refetchAllAppointmentsApis(queryClient);
        },
        onError: (error) => {
          showToast(error.message, "error");
        },
      },
    );
  };
  const isPastDay = moment(
    appointment?.Date,
    "MM/DD/YYYY h:mm:ss A",
    "en",
  ).isBefore(moment());

  const isRelative = appointment?.Relative;

  const appointmentFor = isRelative
    ? appointment?.Relative
    : appointment?.Account;

  const noPatientProfile = !appointment?.IsView;
  const { searchParams, searchAll } = useFilter();

  return (
    <>
      <div
        className={`grid w-full grid-cols-[1fr_3fr_2fr_1fr_2fr] items-center gap-4 rounded-lg border border-primary-click bg-input-field px-6 py-4 transition-all hover:bg-primary-click max-lg:hidden ${className}`}
      >
        {/* DATE */}
        <div className="appointment-date flex items-center gap-2 border-e-2 border-primary-bg pe-6 text-center text-body-text">
          <div className="text-3xl">{appointmentDate.format("DD")}</div>

          <div>
            <div className="text-sm">
              {appointmentDate.format("ddd").toUpperCase()}
            </div>

            <div className="text-sm">
              {appointmentDate.format("MMM").toUpperCase()}
            </div>
          </div>
        </div>

        {/* PATIENT */}
        <div className="flex min-w-0 items-center gap-4">
          <div className="relative shrink-0">
            <ProfileAvatar
              Id={appointment?.Account?.Id}
              src={appointment?.Account?.Image}
              alt={truncateText(appointment?.Account?.FullName, 10)}
              FullName={appointment?.Account?.FullName}
              className="h-12 w-12 rounded-full object-cover"
            />

            {isRelative && (
              <div className="absolute -end-4 bottom-0">
                <ProfileAvatar
                  Id={Number(appointment?.Relative?.Id)}
                  src={appointmentFor?.Image}
                  alt={truncateText(appointmentFor?.FullName, 10)}
                  FullName={appointmentFor?.FullName}
                  size="small"
                  className="h-8 w-8 rounded-full border border-white object-cover"
                />
              </div>
            )}
          </div>

          <div className="flex min-w-0 flex-col justify-center gap-1">
            <p
              className={`line-clamp-1 truncate underline ${
                noPatientProfile ? "" : "cursor-pointer"
              } font-bold`}
              onClick={
                isClickable && !noPatientProfile
                  ? () => {
                      navigate(
                        `/patients_list/${appointmentFor?.Id}/medical-records`,
                      );
                    }
                  : undefined
              }
            >
              {truncateText(appointmentFor?.FullName, 30)}
            </p>

            <div className="truncate text-sm text-body-text">
              {appointmentFor?.Birthday &&
                `${formatAge(appointmentFor?.Birthday)} ${t("years_old")} • `}

              {t(appointmentFor?.Gender?.trim().toLowerCase())}

              {isRelative ? ` • ${t("is_relative")}` : ""}
            </div>
          </div>
        </div>

        {/* TIME + PHONE */}
        <div className="flex flex-col">
          <div className="flex items-center gap-2 text-sm text-primary-text">
            <img src={clockIcon} alt="time" />
            {appointmentDate.format("hh:mm")} -{" "}
            {appointmentDate
              .add(appointment?.Duration, "minutes")
              .format("hh:mm A")}
          </div>

          {appointment?.Account?.PhoneNumber && (
            <div className="mt-1 flex items-center gap-2 text-sm text-primary-text">
              <img src={phoneIcon} alt={t("common.address")} />

              <span dir="ltr">
                {UserInfo.userDetails.Country?.Code}-
                {appointment?.Account?.PhoneNumber}
              </span>
            </div>
          )}
        </div>

        {/* STATUS */}
        <div className="flex justify-center">
          <AppointmentStatusTag statusCode={appointment?.Status} />
        </div>

        {/* ACTIONS */}
        <div className="flex min-w-0 justify-end gap-2">
          {appointment?.Status === AppointmentStatus.Approved ||
          !appointment?.HasAction ||
          appointment?.Status === AppointmentStatus.Canceled ||
          appointment?.Status === AppointmentStatus.Rejected ? (
            <AppointmentActionBtn
              isDisabled={isDisabled}
              action="view_details"
              onClick={(event) => {
                event.stopPropagation();

                navigate(
                  searchAll
                    ? `/appointments/${appointment?.Id}${searchAll}`
                    : `/appointments/${appointment?.Id}`,
                );
              }}
            />
          ) : (
            <>
              <AppointmentActionBtn
                action="reject"
                onClick={(event) => {
                  event.stopPropagation();
                  setIsCancelConfirmOpen(true);
                  setConfirmCancelStatus("Reject");
                }}
              />

              <AppointmentActionBtn
                action="confirm"
                onClick={(event) => {
                  event.stopPropagation();
                  mutate({
                    AppointmentId: appointment?.Id,
                    action: "Approve",
                  } as any);
                }}
              />
            </>
          )}
        </div>
      </div>
      {/* mobile style  */}
      <div
        className="lg:hidden max-md:border-b-2"
        onClick={
          isClickable && !noPatientProfile
            ? () => {
                navigate(`/patients_list/${appointmentFor?.Id}`);
              }
            : undefined
        }
      >
        <div className="flex w-full items-center gap-5">
          <div className="relative">
            <ProfileAvatar
              Id={appointment?.Account?.Id}
              src={appointment?.Account?.Image}
              alt={truncateText(appointment?.Account?.FullName, 10)}
              FullName={appointment?.Account?.FullName}
              className="h-[3.94rem] w-[3.94rem] rounded-full border object-cover"
            />
            {isRelative && (
              <div className="absolute -end-4 bottom-0">
                <ProfileAvatar
                  Id={Number(appointment?.Relative?.Id)}
                  src={appointmentFor?.Image}
                  alt={truncateText(appointmentFor?.FullName, 10)}
                  FullName={appointmentFor?.FullName}
                  size="small"
                  className="h-[2.94rem] w-[2.94rem] rounded-full border border-white object-cover"
                />
              </div>
            )}
          </div>
          <div className="flex w-full flex-col justify-center gap-1">
            <div className="flex items-center justify-between">
              {appointment?.Date && (
                <DateTimeAppointmentBtn date={appointment?.Date} />
              )}
              <AppointmentStatusTag statusCode={appointment?.Status} />
            </div>
            <div className="text-md line-clamp-1 font-bold">
              {truncateText(appointmentFor?.FullName, 20)}
            </div>
            <div className="text-sm text-body-text">
              {appointmentFor?.Birthday &&
                `${formatAge(appointmentFor?.Birthday)} ${t("years_old")}${" "}
               •  `}{" "}
              {t(appointmentFor?.Gender?.trim().toLowerCase())}{" "}
              {isRelative ? ` • ${t("is_relative")}` : ""}
            </div>
          </div>
        </div>
        <div
          className={`flex min-w-[10rem] justify-end gap-2 ${
            (appointment?.Status?.toUpperCase() === AppointmentStatus.Pending ||
              appointment?.Status === AppointmentStatus.Approved) &&
            !isPastDay
              ? "ps-4 lg:ps-0"
              : "hidden lg:invisible lg:flex"
          }`}
        >
          {appointment?.Status === AppointmentStatus.Approved ||
          !appointment?.HasAction ? (
            <GeneralButton
              title={t("cancel_appointment")}
              color="lite"
              className="h-fit !w-1/3 !rounded-lg !bg-error-00 !text-sm !text-error"
              onClick={(event) => {
                event.stopPropagation();
                setIsCancelConfirmOpen(true);
                setConfirmCancelStatus("Cancel");
              }}
            />
          ) : (
            <>
              <GeneralButton
                title={t("reject")}
                color="lite"
                className="h-fit !w-1/3 !rounded-lg !bg-error-00 !text-sm !text-error"
                onClick={(event) => {
                  event.stopPropagation();
                  setIsCancelConfirmOpen(true);
                  setConfirmCancelStatus("Reject");
                }}
              />
              <GeneralButton
                className="h-fit !w-2/3 !rounded-lg !bg-filter-outline !text-sm !text-primary"
                title={t("confirm")}
                color="blue"
                onClick={(event) => {
                  event.stopPropagation();
                  mutate({
                    AppointmentId: appointment?.Id,
                    action: "Approve",
                  } as any);
                }}
              />
            </>
          )}
        </div>
      </div>
      {isCancelConfirmOpen && (
        <AppointmentCancelConfirm
          onSubmit={onSubmit}
          status={confirmCancelStatus}
          isOpen={isCancelConfirmOpen}
          appointmentDate={appointmentDate}
          isLoading={isPending}
          onClose={() => {
            setIsCancelConfirmOpen(false);
          }}
        />
      )}
    </>
  );
};

export default MyAppointmentCard;
